import { useState, useEffect, useCallback } from 'react'
import { fetchAuthSession } from 'aws-amplify/auth'
import axios from 'axios'

const API = import.meta.env.VITE_API_GATEWAY_URL

async function authHeaders() {
    const session = await fetchAuthSession()
    return { Authorization: `Bearer ${session.tokens.idToken.toString()}` }
}

function LeadsManager() {
    const [leads, setLeads]     = useState([])
    const [loading, setLoading] = useState(true)
    const [selected, setSelected] = useState(null)
    const [search, setSearch]   = useState('')
    const [msg, setMsg]         = useState(null)

    const loadLeads = useCallback(async () => {
        setLoading(true)
        try {
            const headers = await authHeaders()
            const res = await axios.get(`${API}/admin/contactos`, { headers })
            setLeads(res.data.leads || [])
        } catch (err) {
            setMsg({ type: 'error', text: 'Error cargando leads: ' + err.message })
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => { loadLeads() }, [loadLeads])

    const filtered = leads.filter(l =>
        l.nombre?.toLowerCase().includes(search.toLowerCase()) ||
        l.email?.toLowerCase().includes(search.toLowerCase()) ||
        l.empresa?.toLowerCase().includes(search.toLowerCase())
    )

    const formatDate = (ts) => {
        if (!ts) return '—'
        // puede ser ISO string o unix timestamp
        const d = isNaN(ts) ? new Date(ts) : new Date(parseInt(ts) * 1000)
        return d.toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' })
    }

    return (
        <div className="admin-section">
            <div className="admin-section-header">
                <div>
                    <h2>📩 Leads y Contactos</h2>
                    <p>Mensajes recibidos del formulario de contacto de ingconia.com</p>
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                    <input
                        className="admin-search"
                        type="text"
                        placeholder="🔍 Buscar lead..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                    <button className="btn-ghost btn-sm" onClick={loadLeads}>↺ Actualizar</button>
                </div>
            </div>

            {msg && (
                <div className={`admin-alert admin-alert--${msg.type}`}>
                    {msg.text} <button onClick={() => setMsg(null)}>✕</button>
                </div>
            )}

            {/* Detalle del lead seleccionado */}
            {selected && (
                <div className="admin-form-card" style={{ marginBottom: '1.5rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <h3>📋 Mensaje de <span className="highlight">{selected.nombre}</span></h3>
                        <button className="btn-ghost btn-sm" onClick={() => setSelected(null)}>✕ Cerrar</button>
                    </div>
                    <div className="form-row" style={{ marginTop: '1rem' }}>
                        <div className="form-group">
                            <label>📧 Email</label>
                            <p style={{ color: '#c3c8d4', margin: 0 }}>{selected.email || '—'}</p>
                        </div>
                        <div className="form-group">
                            <label>🏢 Empresa</label>
                            <p style={{ color: '#c3c8d4', margin: 0 }}>{selected.empresa || '—'}</p>
                        </div>
                        <div className="form-group">
                            <label>📱 Teléfono</label>
                            <p style={{ color: '#c3c8d4', margin: 0 }}>{selected.telefono || '—'}</p>
                        </div>
                        <div className="form-group">
                            <label>🕐 Fecha</label>
                            <p style={{ color: '#c3c8d4', margin: 0 }}>{formatDate(selected.fecha)}</p>
                        </div>
                    </div>
                    <div className="form-group" style={{ marginTop: '0.5rem' }}>
                        <label>💬 Mensaje</label>
                        <div style={{
                            background: 'rgba(255,255,255,0.04)',
                            border: '1px solid rgba(255,255,255,0.08)',
                            borderRadius: '10px',
                            padding: '1rem',
                            color: '#c3c8d4',
                            lineHeight: '1.6',
                            whiteSpace: 'pre-wrap',
                            marginTop: '0.5rem'
                        }}>
                            {selected.mensaje}
                        </div>
                    </div>
                    <div className="form-actions" style={{ marginTop: '1rem' }}>
                        <a
                            href={`mailto:${selected.email}?subject=Re: Tu mensaje en Ingconia`}
                            className="btn-primary"
                            style={{ display: 'inline-block', textDecoration: 'none', padding: '0.5rem 1.25rem', borderRadius: '12px', fontSize: '0.85rem' }}
                        >
                            📧 Responder por email
                        </a>
                    </div>
                </div>
            )}

            {/* Tabla de leads */}
            {loading ? (
                <div className="admin-loading-inline">
                    <div className="admin-spinner-sm"></div> Cargando leads...
                </div>
            ) : (
                <div className="admin-table-wrapper">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>Nombre</th>
                                <th>Email</th>
                                <th>Empresa</th>
                                <th>Teléfono</th>
                                <th>Fecha</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((lead, i) => (
                                <tr key={lead.pk || i}>
                                    <td>
                                        <div className="user-cell">
                                            <div className="user-avatar-sm">{(lead.nombre || 'L').charAt(0).toUpperCase()}</div>
                                            <span>{lead.nombre || '—'}</span>
                                        </div>
                                    </td>
                                    <td><code className="code-badge">{lead.email}</code></td>
                                    <td>
                                        {lead.empresa
                                            ? <span className="company-badge">{lead.empresa}</span>
                                            : <span className="no-value">—</span>
                                        }
                                    </td>
                                    <td><span className="no-value">{lead.telefono || '—'}</span></td>
                                    <td style={{ fontSize: '0.78rem', color: '#8a8fa8' }}>{formatDate(lead.fecha)}</td>
                                    <td className="actions-cell">
                                        <button className="btn-sm btn-primary" onClick={() => setSelected(lead)} title="Ver mensaje">
                                            👁️ Ver
                                        </button>
                                        <a
                                            href={`mailto:${lead.email}?subject=Re: Tu mensaje en Ingconia`}
                                            className="btn-sm btn-ghost"
                                            style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}
                                            title="Responder por email"
                                        >
                                            📧
                                        </a>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {filtered.length === 0 && !loading && (
                        <div className="admin-empty">
                            <span>📭</span>
                            <p>{leads.length === 0 ? 'Aún no hay mensajes de contacto.' : 'No hay leads que coincidan con la búsqueda.'}</p>
                        </div>
                    )}
                </div>
            )}

            <div style={{ marginTop: '1rem', fontSize: '0.75rem', color: '#4b5563', textAlign: 'right' }}>
                {leads.length} mensaje{leads.length !== 1 ? 's' : ''} en total
            </div>
        </div>
    )
}

export default LeadsManager
